import React from 'react';
import { SubscriptionPeriodPricingFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useTranslations } from 'next-intl';
import { twMerge } from 'tailwind-merge';
import { addPeriodToIsoDate } from '@/utils/date-helper';
import { SubscriptionInfoProps } from './types/SubscriptionInfoProps';

export function SubscriptionInfo({
  subscription,
  isLargeText = false,
  isMedText = false,
}: SubscriptionInfoProps) {
  const t = useTranslations('SubscriptionInfo');

  const { formatCurrency } = useCurrencyFormatter();
  const { locale } = useLocalization();

  const getSubscriptionInitialPeriodText = (initialPeriod: SubscriptionPeriodPricingFragment) => {
    const { period, unit, price: priceForPeriod } = initialPeriod;
    if (priceForPeriod === 0) {
      return t('free trial description', { period, unit });
    }
    return t('first payment description', {
      period,
      unit,
      convertedPrice: formatCurrency(priceForPeriod),
    });
  };

  const firstPeriod = subscription?.period.initial || subscription?.period.recurring;

  return (
    <>
      {subscription.period?.initial && (
        <div
          className={twMerge(
            'font-medium text-green',
            isLargeText && 'sm:text-lg md:text-xl',
            isMedText && 'sm:text-md md:text-lg',
          )}
        >
          {getSubscriptionInitialPeriodText(subscription.period.initial)}
        </div>
      )}

      {firstPeriod && (
        <div
          className={twMerge(
            'mb-2 font-light text-neutral-50',
            isLargeText && 'sm:text-lg md:text-xl',
            isMedText && 'sm:text-md md:text-lg',
          )}
        >
          {subscription.period.initial?.price && subscription.period.initial.price > 0
            ? t('firstPaymentToday')
            : t('firstPaymentDate', {
              date: addPeriodToIsoDate(
                new Date().toISOString(),
                firstPeriod.period,
                firstPeriod.unit,
              )?.toLocaleDateString(locale) || '',
            })}
        </div>
      )}
    </>
  );
}
